import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [message, setMessage] = useState('');

  const reference = searchParams.get('reference') || searchParams.get('trxref');

  useEffect(() => {
    if (reference) {
      verifyPayment();
    } else {
      setStatus('failed');
      setMessage('No payment reference found');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const verifyPayment = async () => {
    try {
      const response = await authFetch(`${API}/payments/paystack/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reference }),
      });
      const data = await response.json();

      if (data.success && data.tier) {
        const tierLabel = data.tier.charAt(0).toUpperCase() + data.tier.slice(1);
        setStatus('success');
        if (data.founding) {
          setMessage("You're a Founding member. Studio is yours for life — no renewals, ever.");
          toast.success('Welcome, Founding member.');
        } else {
          setMessage(`Your ${tierLabel} subscription is now active!`);
          toast.success(`Welcome to PANGAJI ${tierLabel}!`);
        }
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2500);
      } else {
        setStatus('failed');
        // `message` names the real cause when a confirmed payment could not be
        // applied (e.g. every Founding seat was claimed first — being refunded).
        setMessage(data.message || 'Payment verification failed. Please contact support if you were charged.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('failed');
      setMessage('Could not verify payment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
      <div className="noise-overlay" />
      
      <div className="max-w-md w-full mx-4 text-center">
        <div className="bg-card border border-border/40 p-12">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-mono text-2xl font-semibold mb-4">
                Verifying Payment
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your subscription...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-mono text-2xl font-semibold mb-4 text-green-500">
                Payment Successful!
              </h1>
              <p className="text-muted-foreground mb-6">
                {message}
              </p>
              <p className="text-sm text-muted-foreground">
                Redirecting to dashboard...
              </p>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
              <h1 className="font-mono text-2xl font-semibold mb-4 text-destructive">
                Payment Failed
              </h1>
              <p className="text-muted-foreground mb-6">
                {message}
              </p>
              <button
                onClick={() => navigate('/upgrade')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
